import { ModuleWithProviders, NgModule } from "@angular/core";
import { EffectsModule } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
	AppStartupContext,
	CORE_CONFIG,
	CoreConfig,
	CoreHttpClient,
	CoreModule,
	STARTUP_CONTEXT_TOKEN,
} from "@obg/ngx.core";
import { CoreContentModule } from "@obg/ngx.core.content";
import { GlobalModule } from "@obg/ngx.global";
import { CookieService, DocumentRef, PlatformService } from "@obg/ngx.global.evo";
import { RouterModule } from "@obg/ngx.router";

import { AccountSettingsEffect } from "./account-settings/account-settings.effect";
import { AffiliateConfigService } from "./affiliate/affiliate.config";
import { AffiliateService, ClientAffiliateService, NoopAffiliateService } from "./affiliate/affiliate.service";
import { BlogEffect } from "./blog/blog.effect";
import { CallingCodeEffect } from "./calling-code/calling-code.effect";
import { CampaignEffect } from "./campaign/campaign.effect";
import { CustomerOfferEffect } from "./campaign/customer-offer/customer-offer.effect";
import { CashDeskEffect } from "./cash-desk/cash-desk.effect";
import { CommonContentEffect } from "./common-content/common-content.effect";
import { CookieNoticeEffect } from "./cookie-notice/cookie-notice.effect";
import { CountryEffect } from "./country/country.effect";
import { CustomerAssessmentsEffect } from "./customer-assessments/customer-assessments.effect";
import { CookieConsentEffect } from "./data-protection/cookie-consent/cookie-consent.effect";
import { DataProtectionConfigService } from "./data-protection/data-protection.config";
import { DataProtectionEffect } from "./data-protection/data-protection.effect";
import { MarketingSettingsEffect } from "./data-protection/marketing-settings/marketing-settings.effect";
import { MarketingSettingsService } from "./data-protection/marketing-settings/marketing-settings.service";
import { DialogEffect } from "./dialog/dialog.effect";
import { FraudPreventionConfigService } from "./fraud-prevention/fraud-prevention.config";
import { FraudPreventionProvider } from "./fraud-prevention/fraud-prevention.model";
import { FraudPreventionService } from "./fraud-prevention/fraud-prevention.service";
import { GroupIBFraudPreventionEffect } from "./fraud-prevention/groupib-fraud-prevention.effect";
import { GroupIBFraudPreventionService } from "./fraud-prevention/groupib-fraud-prevention.service";
import { IovationFraudPreventionService } from "./fraud-prevention/iovation-fraud-prevention.service";
import { NoopFraudPreventionService } from "./fraud-prevention/noop-fraud-prevention.service";
import { ThreatMetrixFraudPreventionService } from "./fraud-prevention/threatmetrix-fraud-prevention.service";
import { GeofencingEffect } from "./geofencing/geofencing.effect";
import { HomeContentWidgetEffect } from "./home-content-widget/home-content-widget.effect";
import { IauiBettingLimitEffect } from "./iaui-betting-limit/iaui-betting-limit.effect";
import { DepositThresholdEffect } from "./kcy/deposit-threshold/deposit-threshold.effect";
import { LoyaltyContentEffect } from "./loyalty/loyalty-content/loyalty-content.effect";
import { LoyaltyEffect } from "./loyalty/loyalty.effect";
import { MissingPasswordEffect } from "./missing-password/missing-password.effect";
import { NationalDataEffect } from "./nationaldata/national-data.effect";
import { NationalityEffect } from "./nationality/nationality.effect";
import { NewsletterUnsubscribeEffect } from "./newsletter-unsubscribe/newsletter-unsubscribe.effect";
import { NotificationEffect } from "./notification/notification.effect";
import { OccupationsEffect } from "./occupations/occupations.effect";
import { NoopOmsService } from "./oms/noop/noop-oms.service";
import { OmsConfigService } from "./oms/oms.config";
import { OmsEffect } from "./oms/oms.effect";
import { OmsService } from "./oms/oms.model";
import { CoreOmsService } from "./oms/oms.service";
import { PersonalDataEffect } from "./personal-data/personal-data.effect";
import { PhoneVerificationEffect } from "./phone-verification/phone-verification.effect";
import { ProductLinkEffect } from "./product-link/product-link.effect";
import { RegionEffect } from "./region/region.effect";
import { BalanceLimitEffect } from "./responsible-gaming/balance-limit/balance-limit.effect";
import { BettingLimitEffect } from "./responsible-gaming/betting-limit/betting-limit.effect";
import { DepositLimitCalculatorEffect } from "./responsible-gaming/deposit-limit-calculator/deposit-limit-calculator.effect";
import { HardSessionEffect } from "./responsible-gaming/hard-session/hard-session.effect";
import { LoginLimitEffect } from "./responsible-gaming/login-limit/login-limit.effect";
import { LogoutCooldownLimitEffect } from "./responsible-gaming/logout-cooldown-limit/logout-cooldown-limit.effect";
import { MultiSessionLimitEffect } from "./responsible-gaming/multi-session-limit/multi-session-limit.effect";
import { ZhhSessionLimitEffect } from "./responsible-gaming/zhh-session-limit/zhh-session-limit.effect";
import { RewardEffect } from "./reward/reward.effect";
import { SmsNotificationSettingsEffect } from "./sms-notification-settings/sms-notification-settings.effect";
import { CommonState } from "./state";
import { SupportEffect } from "./support/support.effect";
import { AccountSettingsTelemetryEffect } from "./telemetry/account-settings/account-settings-telemetry.effect";
import { DataProtectionTelemetryEffect } from "./telemetry/data-protection/data-protection-telemetry.effect";
import { TermsAndConditionsEffect } from "./terms-and-conditions/terms-and-conditions.effect";
import { TwoFactorAuthEffect } from "./two-factor-auth/two-factor-auth.effect";
import { UTIL_PIPES } from "./utils/utils.const";

const COMMON_EFFECTS = [
	OmsEffect,
	AccountSettingsEffect,
	AccountSettingsTelemetryEffect,
	CommonContentEffect,
	BlogEffect,
	CashDeskEffect,
	CustomerAssessmentsEffect,
	DialogEffect,
	LoyaltyEffect,
	LoyaltyContentEffect,
	RewardEffect,
	PhoneVerificationEffect,
	PersonalDataEffect,
	SmsNotificationSettingsEffect,
	SupportEffect,
	NewsletterUnsubscribeEffect,
	NotificationEffect,
	CampaignEffect,
	CustomerOfferEffect,
	BettingLimitEffect,
	BalanceLimitEffect,
	CookieNoticeEffect,
	CookieConsentEffect,
	DataProtectionTelemetryEffect,
	MarketingSettingsEffect,
	DataProtectionEffect,
	MultiSessionLimitEffect,
	CountryEffect,
	HomeContentWidgetEffect,
	ProductLinkEffect,
	CallingCodeEffect,
	NationalityEffect,
	RegionEffect,
	MissingPasswordEffect,
	GeofencingEffect,
	GroupIBFraudPreventionEffect,
	TermsAndConditionsEffect,
	OccupationsEffect,
	HardSessionEffect,
	NationalDataEffect,
	DepositThresholdEffect,
	IauiBettingLimitEffect,
	DepositLimitCalculatorEffect,
	LoginLimitEffect,
	TwoFactorAuthEffect,
	LogoutCooldownLimitEffect,
	ZhhSessionLimitEffect,
];

const COMMON_PIPES = [...UTIL_PIPES];

@NgModule({
	imports: [GlobalModule, CoreModule, CoreContentModule, RouterModule, EffectsModule.forFeature(COMMON_EFFECTS)],
	declarations: COMMON_PIPES,
	exports: COMMON_PIPES,
})
export class CommonModule {
	static forRoot(): ModuleWithProviders<CommonModule> {
		return {
			ngModule: CommonModule,
			providers: [
				AffiliateConfigService,
				{
					provide: OmsService,
					useFactory: _omsServiceFactory,
					deps: [CORE_CONFIG, OmsConfigService, CoreHttpClient],
				},
				{
					provide: AffiliateService,
					useFactory: _affiliateService,
					deps: [PlatformService, AffiliateConfigService, CookieService, CoreHttpClient, DocumentRef, Store],
				},
				{
					provide: FraudPreventionService,
					useFactory: _fraudPreventionServiceFactory,
					deps: [
						FraudPreventionConfigService,
						CoreHttpClient,
						PlatformService,
						CookieService,
						STARTUP_CONTEXT_TOKEN,
						DocumentRef,
					],
				},
				{
					provide: MarketingSettingsService,
					useFactory: _marketingSettingsServiceFactory,
					deps: [CoreHttpClient, DataProtectionConfigService],
				},
			],
		};
	}
}

/**
 * @internal
 */
export function _affiliateService(
	platformService: PlatformService,
	affiliateConfigService: AffiliateConfigService,
	cookieService: CookieService,
	httpClient: CoreHttpClient,
	documentRef: DocumentRef,
	store: Store<CommonState>
) {
	return platformService.isBrowser
		? new ClientAffiliateService(affiliateConfigService, cookieService, httpClient, documentRef, store)
		: new NoopAffiliateService();
}

/**
 * @internal
 */
export function _fraudPreventionServiceFactory(
	config: FraudPreventionConfigService,
	httpClient: CoreHttpClient,
	platformService: PlatformService,
	cookieService: CookieService,
	startupContext: AppStartupContext,
	documentRef: DocumentRef
): FraudPreventionService {
	switch (config.getProvider()) {
		case FraudPreventionProvider.Iovation:
			return new IovationFraudPreventionService(config);
		case FraudPreventionProvider.ThreatMetrix:
			return new ThreatMetrixFraudPreventionService(httpClient, config, platformService);
		case FraudPreventionProvider.GroupIB:
			return new GroupIBFraudPreventionService(
				platformService,
				config,
				cookieService,
				startupContext,
				documentRef
			);
		case FraudPreventionProvider.Noop:
			return new NoopFraudPreventionService();
		default:
			throw new Error("Unsupported Fraud Prevention Provider");
	}
}

/**
 * @internal
 */
export function _marketingSettingsServiceFactory(
	httpClient: CoreHttpClient,
	config: DataProtectionConfigService
): MarketingSettingsService {
	return new MarketingSettingsService(httpClient, config);
}

/**
 * @internal
 */
export function _omsServiceFactory(
	coreConfig: CoreConfig | undefined,
	config: OmsConfigService,
	httpClient: CoreHttpClient
): OmsService {
	return coreConfig?.disableOmsService ? new NoopOmsService() : new CoreOmsService(config, httpClient);
}
